import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useWorkspace } from '../WorkspaceContext';
import { WORKSPACE_ROOT } from '../navigation';
import { WELCOME_TOUR_DONE_EVENT } from './addToHomeScreenUtils';

const tourStorageKey = (businessId) => `spirara.welcomeTour.v1.${businessId || 'default'}`;

export const isWelcomeTourDone = (businessId) => {
  try {
    return localStorage.getItem(tourStorageKey(businessId)) === 'done';
  } catch {
    return false;
  }
};

export const markWelcomeTourDone = (businessId) => {
  try {
    localStorage.setItem(tourStorageKey(businessId), 'done');
  } catch {
    // private mode / blocked storage — ignore
  }
};

const STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to your workspace',
    body: 'A short walkthrough to get clients booking you online. First stop: your public booking page under Manage.',
    cta: 'Set up booking page',
    path: null,
    highlights: ['nav-manage'],
  },
  {
    id: 'booking',
    title: 'Your public booking link',
    body: 'Clients book through this link. Customize the username, then copy or share it — you can also share anytime from Front Desk → Quick actions.',
    cta: 'Next: business profile',
    path: `${WORKSPACE_ROOT}/manage/booking-link`,
    highlights: ['nav-manage', 'manage-tab-booking-link'],
  },
  {
    id: 'profile',
    title: 'Business profile',
    body: 'Add the phone number that should get SMS when someone books, plus an optional cover photo for your booking page.',
    cta: 'Next: services',
    path: `${WORKSPACE_ROOT}/manage/profile`,
    highlights: ['manage-tab-profile'],
  },
  {
    id: 'services',
    title: 'Add your services',
    body: 'List what you offer with duration and price. These show up on your public booking page so clients can choose and pay a deposit.',
    cta: 'Got it',
    path: `${WORKSPACE_ROOT}/manage/services`,
    highlights: ['manage-tab-services'],
  },
];

/**
 * First-login guided tour. Persists dismissal per business in localStorage.
 * Mobile: full-width bottom sheet. Desktop: floating card (bottom-right).
 */
const WorkspaceWelcomeTour = () => {
  const { business } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const step = STEPS[stepIndex] || STEPS[0];
  const isLast = stepIndex >= STEPS.length - 1;
  const progress = useMemo(() => ((stepIndex + 1) / STEPS.length) * 100, [stepIndex]);

  useEffect(() => {
    if (!business?.id || !business?.onboardingComplete) {
      setOpen(false);
      return undefined;
    }
    if (isWelcomeTourDone(business.id)) {
      setOpen(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      setReady(true);
      setOpen(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [business?.id, business?.onboardingComplete]);

  // Ensure route matches the active step (after welcome CTA).
  useEffect(() => {
    if (!open || !ready || !step.path) return;
    if (location.pathname !== step.path) {
      navigate(step.path, { replace: true });
    }
  }, [open, ready, step.path, stepIndex, location.pathname, navigate]);

  // Spotlight + scroll highlighted targets into view.
  useEffect(() => {
    if (!open || !ready) return undefined;

    // Wait a tick so routed pages / tabs mount before querying.
    const timer = setTimeout(() => {
      const targets = (step.highlights || [])
        .map((id) => document.querySelector(`[data-tour="${id}"]`))
        .filter(Boolean);

      targets.forEach((el) => el.setAttribute('data-tour-active', 'true'));
      const primary = targets[targets.length - 1] || targets[0];
      primary?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }, 120);

    return () => {
      clearTimeout(timer);
      document.querySelectorAll('[data-tour-active="true"]').forEach((el) => {
        el.removeAttribute('data-tour-active');
      });
    };
  }, [open, ready, step, location.pathname, stepIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const finish = useCallback(() => {
    markWelcomeTourDone(business?.id);
    setOpen(false);
    try {
      window.dispatchEvent(new CustomEvent(WELCOME_TOUR_DONE_EVENT));
    } catch {
      // ignore
    }
  }, [business?.id]);

  const handlePrimary = () => {
    if (isLast) {
      finish();
      return;
    }
    const nextIndex = stepIndex + 1;
    const next = STEPS[nextIndex];
    setStepIndex(nextIndex);
    if (next?.path) {
      navigate(next.path);
    }
  };

  const handleBack = () => {
    if (stepIndex <= 0) return;
    const prevIndex = stepIndex - 1;
    const prev = STEPS[prevIndex];
    setStepIndex(prevIndex);
    if (prev?.path) {
      navigate(prev.path);
    }
  };

  if (!open || !ready) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center sm:items-end sm:justify-end sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-tour-title"
    >
      <div className="absolute inset-0 bg-ink-950/45" aria-hidden="true" />

      <div className="tour-sheet relative z-[61] w-full max-w-md bg-sand-50 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10">
        <div className="h-1 w-full bg-ink-900/10">
          <div
            className="h-full bg-saffron-500 transition-[width] duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-4 sm:px-6 sm:pt-5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-8 w-8 flex-none items-center justify-center bg-saffron-500 text-ink-900">
              <Sparkles className="h-4 w-4" />
            </span>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-600/60">
              Step {stepIndex + 1} of {STEPS.length}
            </p>
          </div>
          <button
            type="button"
            onClick={finish}
            aria-label="Skip welcome tour"
            className="flex h-9 w-9 flex-none items-center justify-center text-ink-600 transition-colors hover:bg-ink-900/5 hover:text-ink-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 pb-5 sm:px-6 sm:pb-6">
          <h2
            id="welcome-tour-title"
            className="font-display text-2xl font-light tracking-tight text-ink-900 sm:text-[1.75rem]"
          >
            {step.title}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-700/80">{step.body}</p>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row-reverse sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={handlePrimary}
              className="group inline-flex w-full items-center justify-center gap-2 bg-saffron-500 px-5 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 sm:w-auto"
            >
              {step.cta}
              {!isLast && (
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              )}
            </button>

            <div className="flex items-center justify-between gap-3 sm:justify-start">
              {stepIndex > 0 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-2 py-2 text-sm font-medium text-ink-700 transition-colors hover:text-ink-900"
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={finish}
                  className="px-2 py-2 text-sm font-medium text-ink-600/70 transition-colors hover:text-ink-900"
                >
                  Skip for now
                </button>
              )}

              <div className="flex gap-1.5" aria-hidden="true">
                {STEPS.map((item, index) => (
                  <span
                    key={item.id}
                    className={`h-1.5 w-1.5 rounded-full transition-colors ${
                      index === stepIndex ? 'bg-saffron-500' : 'bg-ink-900/15'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="h-[max(0.5rem,env(safe-area-inset-bottom))] sm:hidden" />
      </div>
    </div>
  );
};

export default WorkspaceWelcomeTour;
